import React, { CSSProperties, FC, ReactNode } from 'react';

import { SecondaryText, Text } from '..';

import './ObjectInfo.css';

import { Flex, Tag, TagProps } from '@duxton-web/ui';
import { Link } from 'react-router-dom';

import {
  OBJECTDETAILPAGE_OBJECTTYPE,
  ObjectDetailPage_ObjectTypeKeys,
} from '../ObjectDetailPage/ObjectDetailPage';
import { appendEnvParam } from '@/utils/navigation';

export interface ObjectInfoTagProps extends TagProps {
  title: string;
}

interface OwnProps {
  objectType?: (typeof OBJECTDETAILPAGE_OBJECTTYPE)[ObjectDetailPage_ObjectTypeKeys];
  openUrl?: string;
  icon?: ReactNode;
  name: string;
  altNameTooltip?: string;
  nameShowTooltipWithChildren?: boolean;
  type?: string;
  description?: ReactNode;
  showDescriptionTooltip?: boolean;
  style?: CSSProperties;
  /** tag to be shown beside the label */
  headerTag?: ObjectInfoTagProps;
  /** tags to be shown at the footer */
  footerTags?: ObjectInfoTagProps[];
  /** onClick event upon clicking on name link */
  onClick?: (e) => void;
}

export const ObjectInfo: FC<OwnProps> = ({
  objectType = null,
  openUrl = null,
  icon = null,
  name,
  altNameTooltip,
  nameShowTooltipWithChildren = true,
  type = '',
  description = '',
  showDescriptionTooltip = true,
  style,
  headerTag,
  onClick = () => {},
}) => {
  function getUrl(): string {
    if (!openUrl && !objectType) return '';

    if (openUrl) return openUrl;

    switch (objectType) {
      case OBJECTDETAILPAGE_OBJECTTYPE.VARIABLE:
        return appendEnvParam(`/t/variables/${name}/rollout`);
      default:
        return appendEnvParam(`/t/experiments/${altNameTooltip}/overview`);
    }
  }

  function renderHeaderTag(): ReactNode {
    if (!headerTag || !headerTag?.title) return false;

    const { title, ...headerTagProps } = headerTag;
    let updatedProps: TagProps = { ...headerTagProps, size: 'xsmall' };

    return <Tag {...updatedProps}>{title}</Tag>;
  }

  return (
    <Link
      className="grab-x__object-info-link-wrapper"
      to={getUrl()}
      onClick={(e) => {
        onClick(e);

        if (openUrl) {
          e.stopPropagation();
          e.preventDefault();

          window.open(openUrl, '_blank');
        }
      }}
    >
      <div
        className="grab-x__object-info flex-row-center-aligned half-gap w100"
        style={style}
      >
        {icon && icon}
        <div
          className="flex-col overflowHidden"
          style={{
            flex: '1 1',
            alignItems: 'flex-start',
            width: 'calc(100% - 36px)',
          }}
        >
          <Flex
            align="center"
            gap={8}
            style={{
              width: '100%',
            }}
          >
            <Text
              className={`${!getUrl() ? '' : 'grab_x__object-info-link'}`}
              ellipsis
              tooltip={altNameTooltip || name}
              showTooltipWithChildren={nameShowTooltipWithChildren}
              style={{
                width: '100%',
              }}
            >
              {name}
            </Text>

            {renderHeaderTag()}
          </Flex>

          <div
            className="flex-row-center-aligned"
            style={{
              gap: '4px',
              maxWidth: '100%',
            }}
          >
            {type && (
              <>
                <SecondaryText
                  strong
                  style={{
                    fontSize: '10px',
                    minWidth: 'fit-content',
                  }}
                >
                  {type ? String(type).toUpperCase() : ''}
                </SecondaryText>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="2"
                  height="2"
                  viewBox="0 0 2 2"
                  fill="none"
                >
                  <circle cx="1" cy="1" r="1" fill="#D9D9D9" />
                </svg>
              </>
            )}
            <SecondaryText
              ellipsis
              style={{
                flexGrow: 1,
              }}
              showTooltip={showDescriptionTooltip}
            >
              {description}
            </SecondaryText>
          </div>
        </div>
      </div>
    </Link>
  );
};
